import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	Icon,
	IconButton,
	InputAdornment,
	InputLabel,
	Menu,
	MenuItem,
	Paper,
	Select,
	Stack,
	Switch,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TablePagination,
	TableRow,
	Tabs,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ContentCopy as CopyIcon, Close as CloseIcon } from "@mui/icons-material";
import { useCallback, useEffect, useState } from "react";
import ReactJson from "react-json-view";
import {
	AnalysisQueueService,
	IAnalysisQueueFilters,
	IAnalysisQueueItem,
} from "../../../shared/services/api/integrator/AnalysisQueueService";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { useToast } from "../../../shared/hooks";
import { maskCpfOrCnpj } from "../../../shared/utils/globalMask-document";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const today          = new Date().toISOString().split("T")[0];
const thirtyDaysAgo  = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

const formatDate = (dateStr?: string | null) => {
	if (!dateStr) return "—";
	try { return new Date(dateStr).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }); }
	catch { return dateStr; }
};

const queueStatusColor = (status: string): { color: string; bg: string } => {
	const s = (status ?? "").toLowerCase();
	if (["pending", "pendente", "waiting", "queued"].some((v) => s.includes(v)))   return { color: "#1565c0", bg: "#e3f2fd" };
	if (["processing", "processando", "running"].some((v) => s.includes(v)))        return { color: "#0277bd", bg: "#e1f5fe" };
	if (["completed", "concluido", "done", "success", "finished"].some((v) => s.includes(v))) return { color: "#43b897", bg: "#e9f8f5" };
	if (["refused", "recusado", "rejected", "error", "erro", "failed"].some((v) => s.includes(v))) return { color: "#e36b5b", bg: "#fcedea" };
	if (["stopped", "parado", "cancelled", "canceled"].some((v) => s.includes(v))) return { color: "#ed6c02", bg: "#fff4e5" };
	if (["retry", "retrying"].some((v) => s.includes(v)))                           return { color: "#7b1fa2", bg: "#f3e5f5" };
	return { color: "#616161", bg: "#f5f5f5" };
};

const queueStatusLabel = (status: string): string => {
	const map: Record<string, string> = {
		pending: "Pendente", processing: "Processando", completed: "Concluído",
		success: "Sucesso", error: "Erro", failed: "Falha", refused: "Recusado",
		stopped: "Parado", canceled: "Cancelado", cancelled: "Cancelado",
		retry: "Reprocessando", retrying: "Reprocessando", waiting: "Aguardando", queued: "Na fila",
	};
	return map[(status ?? "").toLowerCase()] ?? status;
};

// ─── Types ────────────────────────────────────────────────────────────────────

type QueueAction =
	| { type: "retrySerasa" | "stopSerasa" | "restart" | "stop" | "delete"; itemId: string; display: string }
	| { type: "reprocessSerasa" };

const actionLabels: Record<string, string> = {
	retrySerasa:     "Reprocessar Serasa",
	stopSerasa:      "Parar Retry Serasa",
	restart:         "Reiniciar na fila",
	stop:            "Parar processamento",
	delete:          "Remover da fila",
	reprocessSerasa: "Reprocessar todos os erros Serasa",
};

// ─── Payload Dialog ───────────────────────────────────────────────────────────

interface PayloadDialogProps {
	item: IAnalysisQueueItem | null;
	onClose: () => void;
}

const PayloadDialog = ({ item, onClose }: PayloadDialogProps) => {
	const [tab, setTab] = useState(0);
	const toast = useToast();
	useEffect(() => { if (item) setTab(0); }, [item]);
	if (!item) return null;

	const current    = tab === 0 ? (item.request_payload ?? null) : (item.response_payload ?? null);
	const payloadStr = current ? JSON.stringify(current, null, 2) : "";

	return (
		<Dialog open onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{ pb: 0 }}>
				<Box display="flex" alignItems="center" justifyContent="space-between">
					<Typography fontWeight={500} color="primary.main" sx={{ fontSize: "0.95rem" }}>
						Payload — {item.analysis_id ?? item.id}
					</Typography>
					<Box display="flex" gap={0.5}>
						{payloadStr && (
							<Tooltip title="Copiar payload">
								<IconButton size="small" onClick={() => {
									navigator.clipboard.writeText(payloadStr);
									toast.present({ type: "success", messageOrContent: "Payload copiado." });
								}}>
									<CopyIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						)}
						<Tooltip title="Fechar">
							<IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
						</Tooltip>
					</Box>
				</Box>
				<Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 1 }}>
					<Tab label="Payload Enviado" sx={{ minHeight: 40, fontSize: "0.8rem" }} />
					<Tab label="Resposta" sx={{ minHeight: 40, fontSize: "0.8rem" }} />
				</Tabs>
			</DialogTitle>
			<Divider />
			<DialogContent sx={{ p: 0 }}>
				<Box sx={{ maxHeight: "60vh", overflow: "auto", backgroundColor: "#1e1e1e", p: 2 }}>
					{current && typeof current === "object" ? (
						<ReactJson src={current as object} displayDataTypes={false} quotesOnKeys={false} collapsed={2} name={false} theme="monokai" />
					) : (
						<Typography sx={{ color: "#888", fontFamily: "monospace", fontSize: 13, fontStyle: "italic" }}>
							{tab === 0 ? "Nenhum payload de envio disponível." : "Nenhuma resposta disponível."}
						</Typography>
					)}
				</Box>
			</DialogContent>
		</Dialog>
	);
};

// ─── Row Action Menu ──────────────────────────────────────────────────────────

interface RowMenuProps {
	item: IAnalysisQueueItem;
	canRetry: boolean;
	canStopSerasa: boolean;
	canRestart: boolean;
	canStop: boolean;
	canDelete: boolean;
	onPayload: () => void;
	onAction: (a: QueueAction) => void;
}

const RowActionMenu = ({ item, canRetry, canStopSerasa, canRestart, canStop, canDelete, onPayload, onAction }: RowMenuProps) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open    = Boolean(anchorEl);
	const display = item.analysis_id ?? item.id;

	const close   = () => setAnchorEl(null);
	const trigger = (a: QueueAction) => { close(); onAction(a); };

	return (
		<>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={close}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				PaperProps={{ sx: { minWidth: 220, borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.12)" } }}
			>
				{/* Visualização */}
				<MenuItem onClick={() => { close(); onPayload(); }} sx={{ gap: 1.5, fontSize: "0.85rem", color: "#0277bd" }}>
					<Icon fontSize="small" sx={{ color: "#0277bd" }}>data_object</Icon>
					Ver Payload / Resposta
				</MenuItem>

				<Divider />

				{/* Reprocessamento */}
				{canRestart && (
					<MenuItem onClick={() => trigger({ type: "restart", itemId: item.id, display })} sx={{ gap: 1.5, fontSize: "0.85rem" }}>
						<Icon fontSize="small" sx={{ color: "#43b897" }}>restart_alt</Icon>
						Reiniciar na fila
					</MenuItem>
				)}
				{canRetry && (
					<MenuItem onClick={() => trigger({ type: "retrySerasa", itemId: item.id, display })} sx={{ gap: 1.5, fontSize: "0.85rem" }}>
						<Icon fontSize="small" sx={{ color: "#7b1fa2" }}>replay</Icon>
						Reprocessar Serasa
					</MenuItem>
				)}

				{(canStop || canStopSerasa) && <Divider />}

				{/* Parada */}
				{canStop && (
					<MenuItem onClick={() => trigger({ type: "stop", itemId: item.id, display })} sx={{ gap: 1.5, fontSize: "0.85rem", color: "#ed6c02" }}>
						<Icon fontSize="small" sx={{ color: "#ed6c02" }}>pause_circle</Icon>
						Parar processamento
					</MenuItem>
				)}
				{canStopSerasa && (
					<MenuItem onClick={() => trigger({ type: "stopSerasa", itemId: item.id, display })} sx={{ gap: 1.5, fontSize: "0.85rem", color: "#ed6c02" }}>
						<Icon fontSize="small" sx={{ color: "#ed6c02" }}>stop_circle</Icon>
						Parar Retry Serasa
					</MenuItem>
				)}

				{canDelete && <Divider />}

				{/* Destrutivo */}
				{canDelete && (
					<MenuItem onClick={() => trigger({ type: "delete", itemId: item.id, display })} sx={{ gap: 1.5, fontSize: "0.85rem", color: "#e36b5b" }}>
						<Icon fontSize="small" sx={{ color: "#e36b5b" }}>delete_outline</Icon>
						Remover da fila
					</MenuItem>
				)}
			</Menu>

			<IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} aria-label="Ações">
				<MoreVertIcon fontSize="small" />
			</IconButton>
		</>
	);
};

// ─── Confirm Dialog ───────────────────────────────────────────────────────────

interface ConfirmDialogProps {
	action: QueueAction | null;
	loading: boolean;
	onConfirm: () => void;
	onClose: () => void;
}

const ConfirmDialog = ({ action, loading, onConfirm, onClose }: ConfirmDialogProps) => {
	if (!action) return null;
	const isDestructive = action.type === "delete" || action.type === "stop";
	const label  = actionLabels[action.type];
	const target = "itemId" in action ? `item "${action.display}"` : "todos os itens com erro Serasa";

	return (
		<Dialog open onClose={onClose} maxWidth="xs" fullWidth>
			<DialogTitle sx={{ fontWeight: 400, color: "primary.main" }}>{label}</DialogTitle>
			<Divider />
			<DialogContent>
				<DialogContentText>
					Confirma a ação <strong>{label}</strong> para {target}?
				</DialogContentText>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
				<Button variant="outlined" onClick={onClose} disabled={loading}>Cancelar</Button>
				<Button
					variant="contained"
					color={isDestructive ? "error" : "primary"}
					onClick={onConfirm}
					disabled={loading}
					startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}
				>
					Confirmar
				</Button>
			</DialogActions>
		</Dialog>
	);
};

// ─── Period helpers ───────────────────────────────────────────────────────────

const getPeriodDates = (period: string, dateIni: string, dateFim: string): { dataini: string; datafim: string } => {
	const fmt = (d: Date) => d.toISOString().split("T")[0];
	if (period === "hoje") return { dataini: today, datafim: today };
	if (period === "7")    return { dataini: fmt(new Date(Date.now() - 7  * 86400000)), datafim: today };
	if (period === "15")   return { dataini: fmt(new Date(Date.now() - 15 * 86400000)), datafim: today };
	if (period === "30")   return { dataini: fmt(new Date(Date.now() - 30 * 86400000)), datafim: today };
	if (period === "60")   return { dataini: fmt(new Date(Date.now() - 60 * 86400000)), datafim: today };
	if (period === "mes")  { const s = new Date(); s.setDate(1);      return { dataini: fmt(s), datafim: today }; }
	if (period === "ano")  { const s = new Date(); s.setMonth(0, 1);  return { dataini: fmt(s), datafim: today }; }
	return { dataini: dateIni, datafim: dateFim };
};

// ─── Main ─────────────────────────────────────────────────────────────────────

export const AnalysisQueueTab = () => {
	const toastHandler = useToast();

	const canView       = checkByProfileGuard(PermissionRolesEnum.IntegratorOptions, PermissionActionsEnum.View);
	const canRetry      = checkByProfileGuard(PermissionRolesEnum.IntegratorOptions, PermissionActionsEnum.QueueRetrySerasa);
	const canStopSerasa = checkByProfileGuard(PermissionRolesEnum.IntegratorOptions, PermissionActionsEnum.QueueStopSerasa);
	const canReprocess  = checkByProfileGuard(PermissionRolesEnum.IntegratorOptions, PermissionActionsEnum.QueueReprocessSerasa);
	const canRestart    = checkByProfileGuard(PermissionRolesEnum.IntegratorOptions, PermissionActionsEnum.QueueRestart);
	const canStop       = checkByProfileGuard(PermissionRolesEnum.IntegratorOptions, PermissionActionsEnum.QueueStop);
	const canDelete     = checkByProfileGuard(PermissionRolesEnum.IntegratorOptions, PermissionActionsEnum.QueueDelete);

	const [items, setItems]         = useState<IAnalysisQueueItem[]>([]);
	const [total, setTotal]         = useState(0);
	const [isLoading, setIsLoading] = useState(false);

	const [periodType,    setPeriodType]    = useState("30");
	const [searchField,   setSearchField]   = useState("document");
	const [searchText,    setSearchText]    = useState("");
	const [serasaOnly,    setSerasaOnly]    = useState(false);
	const [customDateIni, setCustomDateIni] = useState(thirtyDaysAgo);
	const [customDateFim, setCustomDateFim] = useState(today);

	const [query, setQuery]                 = useState<IAnalysisQueueFilters>({ dataini: thirtyDaysAgo, datafim: today, page: 1, limit: 20 });
	const [pendingAction, setPendingAction] = useState<QueueAction | null>(null);
	const [actionLoading, setActionLoading] = useState(false);
	const [payloadItem, setPayloadItem]     = useState<IAnalysisQueueItem | null>(null);

	const load = useCallback(async () => {
		if (!canView) return;
		setIsLoading(true);
		const result = await AnalysisQueueService.listQueue(query);
		if (result instanceof Error) {
			toastHandler.present({ type: "error", messageOrContent: result.message });
		} else {
			setItems(result.data);
			setTotal(result.total);
		}
		setIsLoading(false);
	}, [query, canView]);

	useEffect(() => { load(); }, [load]);

	const handleSearch = () => {
		const dates = getPeriodDates(periodType, customDateIni, customDateFim);
		setQuery({
			page: 1,
			limit: query.limit ?? 20,
			serasa_error_only: serasaOnly,
			...dates,
			...(searchText ? { [searchField]: searchText } as Partial<IAnalysisQueueFilters> : {}),
		});
	};

	const handleClear = () => {
		setPeriodType("30");
		setSearchField("document");
		setSearchText("");
		setSerasaOnly(false);
		setCustomDateIni(thirtyDaysAgo);
		setCustomDateFim(today);
		setQuery({ dataini: thirtyDaysAgo, datafim: today, page: 1, limit: 20 });
	};

	const handleConfirm = async () => {
		if (!pendingAction) return;
		setActionLoading(true);
		let result: void | Error | undefined;
		switch (pendingAction.type) {
			case "retrySerasa":     result = await AnalysisQueueService.retrySerasaItem(pendingAction.itemId);    break;
			case "stopSerasa":      result = await AnalysisQueueService.stopSerasaRetryItem(pendingAction.itemId); break;
			case "restart":         result = await AnalysisQueueService.restartItem(pendingAction.itemId);         break;
			case "stop":            result = await AnalysisQueueService.stopItem(pendingAction.itemId);            break;
			case "delete":          result = await AnalysisQueueService.deleteItem(pendingAction.itemId);          break;
			case "reprocessSerasa": result = await AnalysisQueueService.reprocessSerasaErrors();                  break;
		}
		if (result instanceof Error) {
			toastHandler.present({ type: "error", messageOrContent: result.message });
		} else {
			toastHandler.present({ type: "success", messageOrContent: `${actionLabels[pendingAction.type]} executado com sucesso.` });
			await load();
		}
		setPendingAction(null);
		setActionLoading(false);
	};

	const page0 = (query.page ?? 1) - 1;

	return (
		<Box>
			{/* ── Filtros ───────────────────────────────────────────────────── */}
			<Box
				component={Paper}
				elevation={0}
				sx={{ mx: 1, mt: 2, px: 2, py: 1.5, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}
			>
				{/* Período */}
				<FormControl size="small" sx={{ minWidth: 160 }}>
					<InputLabel>Período</InputLabel>
					<Select value={periodType} label="Período" onChange={(e) => setPeriodType(e.target.value)}>
						<MenuItem value="hoje">Hoje</MenuItem>
						<MenuItem value="7">Últimos 7 dias</MenuItem>
						<MenuItem value="15">Últimos 15 dias</MenuItem>
						<MenuItem value="30">Últimos 30 dias</MenuItem>
						<MenuItem value="60">Últimos 60 dias</MenuItem>
						<MenuItem value="mes">Mês Atual</MenuItem>
						<MenuItem value="ano">Último Ano</MenuItem>
						<MenuItem value="periodo" sx={{ color: "primary.main" }}>Período</MenuItem>
					</Select>
				</FormControl>

				{/* Datas customizadas */}
				{periodType === "periodo" && (
					<>
						<TextField
							label="Data inicial" type="date" size="small"
							InputLabelProps={{ shrink: true }}
							value={customDateIni}
							onChange={(e) => setCustomDateIni(e.target.value)}
							sx={{ minWidth: 155 }}
						/>
						<TextField
							label="Data final" type="date" size="small"
							InputLabelProps={{ shrink: true }}
							value={customDateFim}
							onChange={(e) => setCustomDateFim(e.target.value)}
							sx={{ minWidth: 155 }}
						/>
					</>
				)}

				{/* Pesquisar por */}
				<FormControl size="small" sx={{ minWidth: 170 }}>
					<InputLabel>Pesquisar por</InputLabel>
					<Select value={searchField} label="Pesquisar por" onChange={(e) => setSearchField(e.target.value)}>
						<MenuItem value="document">Documento</MenuItem>
						<MenuItem value="partner_name">Parceiro</MenuItem>
						<MenuItem value="analysis_id">ID Análise</MenuItem>
						<MenuItem value="status">Status</MenuItem>
						<MenuItem value="error_step">Passo de erro</MenuItem>
					</Select>
				</FormControl>

				{/* Input de busca com botão limpar embutido */}
				<TextField
					size="small"
					label="Pesquisar"
					value={searchText}
					onChange={(e) => setSearchText(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && handleSearch()}
					sx={{ minWidth: 280 }}
					InputProps={{
						endAdornment: searchText ? (
							<InputAdornment position="end">
								<IconButton size="small" edge="end" onClick={() => setSearchText("")}>
									<Icon fontSize="small">clear</Icon>
								</IconButton>
							</InputAdornment>
						) : undefined,
					}}
				/>

				{/* Buscar */}
				<Button variant="contained" size="small" startIcon={<Icon fontSize="small">search</Icon>} onClick={handleSearch}>
					Buscar
				</Button>

				{/* Toggle Serasa */}
				<FormControlLabel
					sx={{ ml: 1 }}
					control={
						<Switch size="small" checked={serasaOnly} onChange={(e) => setSerasaOnly(e.target.checked)} />
					}
					label={<Typography variant="body2">Somente erros Serasa</Typography>}
				/>

				{/* Limpar */}
				<Box sx={{ ml: "auto" }}>
					<Button variant="text" size="small" color="inherit" startIcon={<Icon fontSize="small">clear</Icon>} onClick={handleClear}>
						Limpar
					</Button>
				</Box>
			</Box>

			{/* ── Tabela ────────────────────────────────────────────────────── */}
			<TableContainer
				component={Paper}
				variant="elevation"
				sx={{
					mt: 1,
					width: "96%",
					px: { xs: 1, sm: 3 },
					py: { xs: 2, sm: 3 },
					ml: 1,
					borderRadius: 2,
					overflow: "auto",
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
					<Typography variant="caption" color="text.secondary">
						{total} {total === 1 ? "registro" : "registros"}
					</Typography>
					{canReprocess && (
						<Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
							<Button
								variant="outlined"
								size="small"
								color="warning"
								startIcon={<Icon fontSize="small">replay</Icon>}
								onClick={() => setPendingAction({ type: "reprocessSerasa" })}
							>
								Reprocessar todos os erros Serasa
							</Button>
						</Box>
					)}
				</Box>
				<Table
					size="small"
					stickyHeader
					aria-label="fila de análise"
					sx={{
						"& .MuiTableCell-head": { backgroundColor: "grey.100", color: "text.secondary", borderBottomColor: "#f1f1f1" },
						"& .MuiTableCell-root": { borderBottomColor: "#f1f1f1" },
						"& tbody .MuiTableCell-root": { py: 1 },
					}}
				>
					<TableHead>
						<TableRow>
							<TableCell sx={{ minWidth: 200 }}><Typography variant="button" color="text.secondary">ID / Análise</Typography></TableCell>
							<TableCell sx={{ minWidth: 130 }}><Typography variant="button" color="text.secondary">Documento</Typography></TableCell>
							<TableCell sx={{ minWidth: 120 }}><Typography variant="button" color="text.secondary">Parceiro</Typography></TableCell>
							<TableCell sx={{ minWidth: 130 }}><Typography variant="button" color="text.secondary">Status</Typography></TableCell>
							<TableCell sx={{ minWidth: 80, textAlign: "center" }}><Typography variant="button" color="text.secondary">Tentativas</Typography></TableCell>
							<TableCell sx={{ minWidth: 150 }}><Typography variant="button" color="text.secondary">Passo / Erro</Typography></TableCell>
							<TableCell sx={{ minWidth: 140 }}><Typography variant="button" color="text.secondary">Datas</Typography></TableCell>
							<TableCell align="right"><Typography variant="button" color="text.secondary">Ações</Typography></TableCell>
						</TableRow>
					</TableHead>

					<TableBody>
						{isLoading && (
							<TableRow>
								<TableCell colSpan={8} align="center" sx={{ py: 6 }}>
									<Stack alignItems="center" gap={1}>
										<CircularProgress size={32} />
										<Typography variant="body2" color="text.secondary">Carregando fila de análise...</Typography>
									</Stack>
								</TableCell>
							</TableRow>
						)}

						{!isLoading && items.length === 0 && (
							<TableRow>
								<TableCell colSpan={8} align="center" sx={{ py: 6 }}>
									<Stack alignItems="center" gap={1}>
										<Icon sx={{ fontSize: 40, color: "text.disabled" }}>inbox</Icon>
										<Typography color="text.secondary">Nenhum item encontrado na fila.</Typography>
									</Stack>
								</TableCell>
							</TableRow>
						)}

						{!isLoading && items.map((item) => {
							const si          = queueStatusColor(item.status);
							const statusLbl   = queueStatusLabel(item.status);
							const display     = item.analysis_id ?? item.id;
							const doc         = item.queue_document ?? item.document ?? "";
							const attempts    = item.attempt_count ?? 0;
							const maxAttempts = item.max_attempts ?? 5;

							return (
								<TableRow key={item.id} hover>
									{/* ID / Análise */}
									<TableCell>
										{item.queue_number && (
											<Typography variant="caption" sx={{ fontSize: "0.6rem", color: "primary.main", fontWeight: 700, display: "block", mb: 0.25 }}>
												#{item.queue_number}
											</Typography>
										)}
										<Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: "0.72rem", fontFamily: "monospace" }}>
											{display}
										</Typography>
										{item.analysis_id && item.id !== item.analysis_id && (
											<Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.63rem", fontFamily: "monospace" }}>
												{item.id}
											</Typography>
										)}
									</TableCell>

									{/* Documento */}
									<TableCell>
										<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
											{doc ? maskCpfOrCnpj(doc) : "—"}
										</Typography>
									</TableCell>

									{/* Parceiro */}
									<TableCell>
										<Tooltip title={item.partner_name ?? ""} placement="top">
											<Typography
												variant="body2"
												color="text.secondary"
												noWrap
												sx={{ fontSize: "0.8rem", maxWidth: 115, display: "block" }}
											>
												{item.partner_name ?? "—"}
											</Typography>
										</Tooltip>
									</TableCell>

									{/* Status */}
									<TableCell>
										<Stack direction="column" gap={0.5} alignItems="flex-start">
											<Chip
												size="small"
												label={statusLbl}
												sx={{
													color: si.color, fontWeight: 500, background: si.bg,
													border: "0.5px solid #f1f1f1", borderRadius: "999px",
													height: 22, "& .MuiChip-label": { px: 1.25, fontSize: "0.7rem" },
												}}
											/>
											{item.serasa_error && (
												<Chip
													size="small"
													label="Serasa"
													sx={{
														color: "#c62828", background: "#ffebee",
														border: "0.5px solid #f1f1f1", borderRadius: "999px",
														height: 20, "& .MuiChip-label": { px: 1.25, fontSize: "0.65rem" },
													}}
												/>
											)}
											{item.partner_reason && (
												<Tooltip title={item.partner_reason} placement="top">
													<Typography
														variant="caption"
														color="text.secondary"
														noWrap
														sx={{ fontSize: "0.62rem", maxWidth: 120, display: "block", cursor: "default" }}
													>
														{item.partner_reason}
													</Typography>
												</Tooltip>
											)}
										</Stack>
									</TableCell>

									{/* Tentativas */}
									<TableCell align="center">
										<Box
											sx={{
												display: "inline-flex", alignItems: "baseline", gap: 0.3,
												px: 1, py: 0.25, borderRadius: 1,
												backgroundColor: attempts >= maxAttempts ? "#fcedea" : "#f5f5f5",
											}}
										>
											<Typography
												variant="caption"
												fontWeight={700}
												sx={{ fontSize: "0.8rem", color: attempts >= maxAttempts ? "#e36b5b" : "text.primary" }}
											>
												{attempts}
											</Typography>
											<Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.68rem" }}>
												/{maxAttempts}
											</Typography>
										</Box>
									</TableCell>

									{/* Passo / Erro */}
									<TableCell>
										{item.error_step ? (
											<Chip
												size="small"
												label={item.error_step}
												sx={{
													fontSize: "0.65rem", height: 20, mb: 0.4,
													background: "#f5f5f5", border: "0.5px solid #e0e0e0",
													borderRadius: "999px", "& .MuiChip-label": { px: 1 },
												}}
											/>
										) : (
											<Typography variant="body2" color="text.disabled" sx={{ fontSize: "0.75rem" }}>—</Typography>
										)}
										{item.last_error && (
											<Tooltip title={item.last_error} placement="top">
												<Typography
													variant="caption"
													color="error.main"
													noWrap
													sx={{ fontSize: "0.62rem", maxWidth: 140, display: "block", cursor: "default" }}
												>
													{item.last_error}
												</Typography>
											</Tooltip>
										)}
									</TableCell>

									{/* Datas */}
									<TableCell>
										<Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", display: "block" }}>
											{formatDate(item.created_at)}
										</Typography>
										{item.updated_at && (
											<Typography variant="caption" sx={{ fontSize: "0.72rem", color: "error.main", display: "block" }}>
												{formatDate(item.updated_at)}
											</Typography>
										)}
									</TableCell>

									{/* Ações */}
									<TableCell align="right">
										<RowActionMenu
											item={item}
											canRetry={canRetry}
											canStopSerasa={canStopSerasa}
											canRestart={canRestart}
											canStop={canStop}
											canDelete={canDelete}
											onPayload={() => setPayloadItem(item)}
											onAction={setPendingAction}
										/>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>

					{!isLoading && items.length > 0 && (
						<TableFooter>
							<TableRow>
								<TablePagination
									colSpan={8}
									count={total}
									page={page0}
									rowsPerPage={query.limit ?? 20}
									rowsPerPageOptions={[10, 20, 50]}
									onPageChange={(_, p) => setQuery((q) => ({ ...q, page: p + 1 }))}
									onRowsPerPageChange={(e) => setQuery((q) => ({ ...q, limit: Number(e.target.value), page: 1 }))}
									labelRowsPerPage="Por página:"
									labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
								/>
							</TableRow>
						</TableFooter>
					)}
				</Table>
			</TableContainer>

			<ConfirmDialog
				action={pendingAction}
				loading={actionLoading}
				onClose={() => !actionLoading && setPendingAction(null)}
				onConfirm={handleConfirm}
			/>

			<PayloadDialog item={payloadItem} onClose={() => setPayloadItem(null)} />
		</Box>
	);
};
